import classNames from 'styles/utils/classNames';
import { BookingSlot } from './types';

interface Props {
  slot: BookingSlot;
  isSelected: boolean;
  /** False for an available slot that would break the contiguous run. */
  isSelectable: boolean;
  /** True when the venue-local start instant has passed. */
  isPast?: boolean;
  onSelect: (slotId: string) => void;
  /**
   * Which of the two chips the design draws: `grid` is "Filter elements", the
   * booking grid's chip, grey until it is picked; `card` is "Book Time slot",
   * outlined, always dark, and taller on desktop, used by the home feed's club
   * cards.
   */
  appearance?: 'grid' | 'card';
}

const APPEARANCE_CLASSES: Record<NonNullable<Props['appearance']>, string> = {
  grid: 'h-8 rounded-md px-ds-md',
  card: 'h-8 rounded-lg border border-color-border-input-lightmode px-ds-sm dark:border-color-border-input-darkmode lg:h-10 lg:px-ds-md',
};

/**
 * One bookable increment.
 *
 * The discount tag deliberately overhangs the bottom-right corner, as in the
 * design — the chip is `overflow-visible` so it is not clipped, and the grid
 * leaves enough row gap for it.
 */
export default function SlotChip({
  slot,
  isSelected,
  isSelectable,
  isPast = false,
  onSelect,
  appearance = 'grid',
}: Props) {
  const isDisabled = isPast || !slot.isAvailable || (!isSelectable && !isSelected);
  // A card chip is an offer rather than a toggle, so it is dark from the start.
  const isDark = !isPast && (isSelected || appearance === 'card');

  return (
    <button
      type="button"
      disabled={isDisabled}
      aria-pressed={isSelected}
      onClick={() => onSelect(slot.id)}
      className={classNames(
        'typography-product-chips-filters relative flex items-center justify-center whitespace-nowrap py-ds-sm text-center transition-colors',
        APPEARANCE_CLASSES[appearance],
        isDark
          ? 'bg-color-bg-lightmode-invert text-color-text-lightmode-invert hover:bg-color-bg-lightmode-invert-highlighted dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert'
          : isDisabled
          ? 'cursor-not-allowed bg-color-bg-lightmode-tertiary text-color-text-lightmode-disabled dark:bg-color-bg-darkmode-tertiary dark:text-color-text-darkmode-disabled'
          : 'bg-color-bg-lightmode-secondary text-color-text-lightmode-secondary hover:bg-color-bg-lightmode-highlighted dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-secondary',
      )}
    >
      {slot.label}
      {slot.discountPercent !== null && (
        <img
          src="/images/court-booking/discount-tag.svg"
          alt={`${slot.discountPercent}% off`}
          width={20}
          height={20}
          className="absolute -bottom-2 right-0 h-4 w-4 lg:h-5 lg:w-5"
        />
      )}
    </button>
  );
}
